/**
 * idMap — bidirectional map between legacy `_id` strings and InstantDB UUIDs.
 *
 * Context: The existing mock data uses string ids like "inv_001" or
 * "dlr_001". InstantDB requires UUIDs as primary keys (returned by `id()`).
 * We preserve the legacy id on each entity as a unique-indexed `_id` field,
 * and keep an in-memory map so transactions can resolve a legacy id to its
 * InstantDB UUID without a network round-trip.
 *
 * The map is rebuilt from query results on every load — InstantDB returns
 * both `id` (UUID) and `_id` (legacy) for every entity, so we can populate
 * the map in O(n) from the very first useQuery.
 *
 * Scope: per entity type. We use a single shared map keyed by
 * `${entityName}:${_id}` to avoid 40+ separate Maps.
 *
 * For new entities (created locally without a legacy id), we use the UUID
 * as both `id` and `_id`. Lookups by either succeed.
 */

const map = new Map<string, string>();

function key(entityName: string, legacyId: string): string {
  return `${entityName}:${legacyId}`;
}

/** Register a legacy-id → UUID mapping. Safe to call repeatedly. */
export function setIdMapping(
  entityName: string,
  legacyId: string,
  uuid: string,
): void {
  if (!legacyId || !uuid) return;
  map.set(key(entityName, legacyId), uuid);
}

/** Bulk-register many mappings (e.g. after a query returns). */
export function bulkSetIdMappings(
  entityName: string,
  entries: ReadonlyArray<{ _id: string; id: string }>,
): void {
  for (const e of entries) {
    if (e._id && e.id) {
      map.set(key(entityName, e._id), e.id);
    }
  }
}

/**
 * Look up the InstantDB UUID for a legacy `_id`.
 * Returns the legacy id back as a fallback if no mapping is registered —
 * this is the right behavior for the FIRST write of a new entity: the row
 * doesn't exist in InstantDB yet, so we let the transact step's `update`
 * (which acts as upsert) create it with the legacy id as the UUID. But
 * legacy mock ids ("inv_001") are NOT valid UUIDs. So instead we generate
 * a UUID on first write and persist the mapping.
 *
 * For correctness, callers should use `resolveOrCreateUuid()` for writes.
 */
export function lookupUuid(
  entityName: string,
  legacyId: string,
): string | undefined {
  return map.get(key(entityName, legacyId));
}

/** Clear the entire id map. Used by tests and the demo "reset all data" flow. */
export function clearIdMap(): void {
  map.clear();
}

/** Dev-only: dump the current map for debugging. */
export function _dumpIdMap(): Map<string, string> {
  return new Map(map);
}
